import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, Video, Film, Smartphone, Crown, Megaphone, Play, Sparkles, RefreshCw, Edit2, Check, Image, AlertTriangle } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { BottomNav } from '../components/BottomNav';

const VIDEO_TYPES = [
  { id: 'product_showcase', label: 'Product Showcase', icon: Video, desc: 'Product pages & stores', color: 'bg-blue-50 text-blue-700' },
  { id: 'instagram_reel', label: 'Instagram Reel', icon: Smartphone, desc: '9:16 vertical reel', color: 'bg-pink-50 text-pink-700' },
  { id: 'social_video', label: 'Social Video', icon: Play, desc: 'Facebook & social sharing', color: 'bg-green-50 text-green-700' },
  { id: 'luxury_film', label: 'Luxury Brand Film', icon: Crown, desc: 'Cinematic premium film', color: 'bg-purple-50 text-purple-700' },
  { id: 'promotional', label: 'Promotional', icon: Megaphone, desc: 'Offers & launches', color: 'bg-orange-50 text-orange-700' },
];

const STEPS = ['type', 'strategy', 'script', 'scenes', 'review'];
const STATUS_TO_STEP = { draft: 'type', strategy: 'strategy', scripted: 'script', planned: 'review', completed: 'review' };

export const VideoCommerceScreen = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [product, setProduct] = useState(null);
  const [project, setProject] = useState(null);
  const [step, setStep] = useState('type');
  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingScript, setEditingScript] = useState(false);
  const [scriptEdits, setScriptEdits] = useState({});
  const [assets, setAssets] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);

  useEffect(() => {
    fetchProduct();
    fetchAssets();
    // Resume existing project if projectId passed via location state
    const resumeId = location.state?.projectId;
    if (resumeId) {
      resumeProject(resumeId);
    }
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const res = await api.get(`/products/${productId}`);
      setProduct(res.data.product);
    } catch { navigate('/my-products'); }
  };

  const fetchAssets = async () => {
    try {
      const res = await api.get(`/products/${productId}/visual-assets`);
      setAssets((res.data.assets || []).filter(a => a.status === 'completed'));
    } catch {}
  };

  const resumeProject = async (pid) => {
    try {
      const res = await api.get(`/video-projects/${pid}`);
      const p = res.data.project;
      setProject(p);
      setSelectedType(p.video_type);
      // Jump to appropriate step based on status
      const resumeStep = STATUS_TO_STEP[p.status] || 'type';
      // If strategy exists, allow going to script step
      if (p.strategy && !p.script) setStep('script');
      else if (p.script && !p.scenes) setStep('scenes');
      else if (p.scenes) setStep('review');
      else if (p.strategy) setStep('strategy');
      else setStep(resumeStep);
    } catch { /* project not found, start fresh */ }
  };

  const createProject = async () => {
    if (!selectedType) { toast.error('Select a video type'); return; }
    setLoading(true);
    try {
      const res = await api.post('/video-projects', { product_id: productId, video_type: selectedType });
      setProject(res.data.project);
      setStep('strategy');
      toast.success('Project created');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };

  const generateStrategy = async (forceNew = false) => {
    if (!project) return;
    setLoading(true);
    try {
      const url = `/video-projects/${project.id}/strategy${forceNew ? '?force_new=true' : ''}`;
      const res = await api.post(url, {}, { timeout: 60000 });
      const strategy = res.data.strategy;
      if (!strategy || typeof strategy !== 'object') {
        toast.error('Strategy generation returned empty result. Please try again.');
        setLoading(false);
        return;
      }
      setProject(p => ({ ...p, strategy, status: 'strategy' }));
      if (res.data.reused) toast.info('Strategy reused from cache');
      else toast.success('Strategy generated');
      setStep('script');
    } catch (e) { toast.error('Strategy generation failed'); }
    setLoading(false);
  };

  const generateScript = async (forceNew = false) => {
    if (!project) return;
    setLoading(true);
    try {
      const url = `/video-projects/${project.id}/script${forceNew ? '?force_new=true' : ''}`;
      const res = await api.post(url, {}, { timeout: 60000 });
      const script = res.data.script;
      if (!script || typeof script !== 'object') {
        toast.error('Script generation returned empty result. Please try again.');
        setLoading(false);
        return;
      }
      setProject(p => ({ ...p, script, status: 'scripted' }));
      setScriptEdits(script);
      if (res.data.reused) toast.info('Script reused from cache');
      else toast.success('Script generated');
      setStep('scenes');
    } catch (e) { toast.error('Script generation failed'); }
    setLoading(false);
  };

  const saveScriptEdits = async () => {
    try {
      await api.patch(`/video-projects/${project.id}/script`, scriptEdits);
      setProject(p => ({ ...p, script: { ...p.script, ...scriptEdits } }));
      setEditingScript(false);
      toast.success('Script updated');
    } catch { toast.error('Failed to save'); }
  };

  const generateScenes = async (forceNew = false) => {
    if (!project) return;
    setLoading(true);
    try {
      const url = `/video-projects/${project.id}/scenes${forceNew ? '?force_new=true' : ''}`;
      const res = await api.post(url, { project_id: project.id, selected_asset_ids: selectedAssets }, { timeout: 60000 });
      const scenes = res.data.scenes;
      if (!scenes || !Array.isArray(scenes)) {
        toast.error('Scene generation returned empty result. Please try again.');
        setLoading(false);
        return;
      }
      setProject(p => ({ ...p, scenes, status: 'planned' }));
      if (res.data.reused) toast.info('Scene plan reused from cache');
      else toast.success('Scene plan generated');
      setStep('review');
    } catch (e) { toast.error('Scene generation failed'); }
    setLoading(false);
  };

  if (!product) return <div className="min-h-screen bg-[#F0F2F5] flex items-center justify-center"><p>Loading...</p></div>;

  const stepIndex = STEPS.indexOf(step);

  return (
    <div className="min-h-screen bg-[#F0F2F5] pb-20">
      <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-xl">
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-3 flex items-center gap-3">
          <button onClick={() => step === 'type' ? navigate(-1) : setStep(STEPS[Math.max(0, stepIndex - 1)])} data-testid="video-back-btn"><ChevronLeft size={24} /></button>
          <div className="flex-1"><h1 className="text-base font-bold">Video Studio</h1><p className="text-[10px] opacity-80">{product.ai_title || product.name || 'Product'}</p></div>
        </div>

        {/* Progress */}
        <div className="px-4 pt-3 pb-1 flex gap-1">
          {STEPS.map((s, i) => <div key={s} className={`flex-1 h-1 rounded-full ${i <= stepIndex ? 'bg-purple-500' : 'bg-gray-200'}`} />)}
        </div>

        <div className="p-4 space-y-4">

          {/* STEP 1: Video Type */}
          {step === 'type' && (
            <>
              <h2 className="text-lg font-bold text-slate-800">Choose Video Type</h2>
              <div className="space-y-2">
                {VIDEO_TYPES.map(vt => (
                  <button key={vt.id} onClick={() => setSelectedType(vt.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${selectedType === vt.id ? 'border-purple-500 bg-purple-50' : 'border-gray-100'}`}
                    data-testid={`vtype-${vt.id}`}>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${vt.color}`}><vt.icon size={18} /></div>
                    <div><p className="text-sm font-semibold text-slate-800">{vt.label}</p><p className="text-[10px] text-gray-500">{vt.desc}</p></div>
                  </button>
                ))}
              </div>
              <button onClick={createProject} disabled={!selectedType || loading}
                className="w-full h-14 bg-purple-600 text-white rounded-xl text-base font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50"
                data-testid="create-project-btn">
                {loading ? <RefreshCw size={18} className="animate-spin" /> : <Sparkles size={18} />} Continue
              </button>
            </>
          )}

          {/* STEP 2: Strategy */}
          {step === 'strategy' && (
            <>
              <h2 className="text-lg font-bold text-slate-800">Video Strategy</h2>
              {project?.strategy ? (
                <div className="space-y-3">
                  {['concept', 'target_audience', 'key_message', 'visual_style', 'music_mood', 'hook'].map(k => project.strategy[k] && (
                    <div key={k} className="bg-gray-50 rounded-lg p-3">
                      <p className="text-[10px] text-gray-500 capitalize">{k.replace('_', ' ')}</p>
                      <p className="text-sm text-slate-700">{project.strategy[k]}</p>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <button onClick={() => setStep('script')} className="flex-1 h-12 bg-purple-600 text-white rounded-xl font-bold text-sm active:scale-95" data-testid="continue-to-script-btn">Continue to Script</button>
                    <button onClick={() => generateStrategy(true)} className="h-12 px-4 bg-gray-100 rounded-xl text-sm font-medium text-gray-600" data-testid="regen-strategy-btn"><RefreshCw size={14} /></button>
                  </div>
                </div>
              ) : (
                <button onClick={() => generateStrategy(false)} disabled={loading}
                  className="w-full h-14 bg-purple-600 text-white rounded-xl text-base font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="gen-strategy-btn">
                  {loading ? <><RefreshCw size={18} className="animate-spin" /> Generating...</> : <><Sparkles size={18} /> Generate Strategy</>}
                </button>
              )}
            </>
          )}

          {/* STEP 3: Script */}
          {step === 'script' && (
            <>
              <h2 className="text-lg font-bold text-slate-800">Video Script</h2>
              {project?.script ? (
                <div className="space-y-3">
                  {editingScript ? (
                    <>
                      {['hook', 'main_message', 'product_benefits', 'cta', 'full_script'].map(k => (
                        <div key={k}>
                          <label className="text-[10px] text-gray-500 capitalize block mb-1">{k.replace('_', ' ')}</label>
                          <textarea value={scriptEdits[k] || ''} onChange={e => setScriptEdits(p => ({...p, [k]: e.target.value}))}
                            rows={k === 'full_script' ? 4 : 2} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none resize-none" data-testid={`script-edit-${k}`} />
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <button onClick={saveScriptEdits} className="flex-1 h-10 bg-purple-600 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-1" data-testid="save-script-btn"><Check size={14} /> Save</button>
                        <button onClick={() => setEditingScript(false)} className="px-4 h-10 bg-gray-100 rounded-lg text-sm text-gray-600" data-testid="cancel-script-btn">Cancel</button>
                      </div>
                    </>
                  ) : (
                    <>
                      {['hook', 'main_message', 'product_benefits', 'cta'].map(k => project.script[k] && (
                        <div key={k} className="bg-gray-50 rounded-lg p-3">
                          <p className="text-[10px] text-gray-500 capitalize">{k.replace('_', ' ')}</p>
                          <p className="text-sm text-slate-700">{project.script[k]}</p>
                        </div>
                      ))}
                      {project.script.full_script && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-[10px] text-gray-500">Full Script</p>
                          <p className="text-xs text-slate-600 leading-relaxed">{project.script.full_script}</p>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <button onClick={() => { setEditingScript(true); setScriptEdits(project.script || {}); }} className="flex-1 h-10 bg-white border border-purple-300 text-purple-600 rounded-lg text-sm font-medium flex items-center justify-center gap-1" data-testid="edit-script-btn"><Edit2 size={14} /> Edit</button>
                        <button onClick={() => generateScript(true)} className="h-10 px-3 bg-gray-100 rounded-lg text-gray-600" data-testid="regen-script-btn"><RefreshCw size={14} /></button>
                      </div>
                      <button onClick={() => setStep('scenes')} className="w-full h-12 bg-purple-600 text-white rounded-xl font-bold text-sm active:scale-95" data-testid="continue-to-scenes-btn">Continue to Scenes</button>
                    </>
                  )}
                </div>
              ) : (
                <button onClick={() => generateScript(false)} disabled={loading}
                  className="w-full h-14 bg-purple-600 text-white rounded-xl text-base font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="gen-script-btn">
                  {loading ? <><RefreshCw size={18} className="animate-spin" /> Generating...</> : <><Sparkles size={18} /> Generate Script</>}
                </button>
              )}
            </>
          )}

          {/* STEP 4: Scenes */}
          {step === 'scenes' && (
            <>
              <h2 className="text-lg font-bold text-slate-800">Scene Plan</h2>

              {/* Asset Selection */}
              {assets.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Select visual assets for your video:</p>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {assets.map(a => (
                      <button key={a.id} onClick={() => setSelectedAssets(prev => prev.includes(a.id) ? prev.filter(x => x !== a.id) : [...prev, a.id])}
                        data-testid={`asset-select-${a.id}`}
                        className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${selectedAssets.includes(a.id) ? 'border-purple-500' : 'border-gray-200'}`}>
                        {a.storage_key && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${a.storage_key}`} alt="" className="w-full h-full object-cover" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {project?.scenes && Array.isArray(project.scenes) ? (
                <div className="space-y-2">
                  {project.scenes.map((scene, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold">{scene.scene_number || i + 1}</span>
                        <span className="text-xs text-gray-500">{scene.duration}</span>
                        <span className="text-[10px] bg-purple-50 text-purple-600 px-1.5 rounded">{scene.purpose}</span>
                      </div>
                      <p className="text-xs text-slate-700">{scene.visual_description}</p>
                      {scene.camera_direction && <p className="text-[10px] text-gray-400 mt-0.5">Camera: {scene.camera_direction}</p>}
                      {scene.text_overlay && <p className="text-[10px] text-purple-600 mt-1 italic">"{scene.text_overlay}"</p>}
                    </div>
                  ))}
                  <button onClick={() => setStep('review')} className="w-full h-12 bg-purple-600 text-white rounded-xl font-bold text-sm active:scale-95" data-testid="continue-to-review-btn">Review Project</button>
                </div>
              ) : (
                <button onClick={() => generateScenes(false)} disabled={loading}
                  className="w-full h-14 bg-purple-600 text-white rounded-xl text-base font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="gen-scenes-btn">
                  {loading ? <><RefreshCw size={18} className="animate-spin" /> Planning...</> : <><Sparkles size={18} /> Generate Scene Plan</>}
                </button>
              )}
            </>
          )}

          {/* STEP 5: Review */}
          {step === 'review' && project && (
            <>
              <h2 className="text-lg font-bold text-slate-800">Review Your Video Project</h2>
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                <p className="text-xs text-purple-600 font-medium">Video Type</p>
                <p className="text-sm font-bold text-purple-800 capitalize">{(project.video_type || '').replace('_', ' ')}</p>
              </div>
              {project.strategy?.concept && (
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-[10px] text-gray-500">Concept</p><p className="text-sm text-slate-700">{project.strategy.concept}</p></div>
              )}
              {project.script?.hook && (
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-[10px] text-gray-500">Hook</p><p className="text-sm text-slate-700">{project.script.hook}</p></div>
              )}
              {project.script?.cta && (
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-[10px] text-gray-500">Call to Action</p><p className="text-sm text-slate-700">{project.script.cta}</p></div>
              )}
              {project.scenes && <p className="text-xs text-gray-500">{project.scenes.length} scenes planned</p>}

              {/* AI Disclaimer */}
              <div className="flex gap-2 bg-amber-50 rounded-lg p-3 border border-amber-200">
                <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-[10px] text-amber-700">AI video generation requires a configured video provider. Video will be available when provider credentials are set up.</p>
              </div>

              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 text-center border border-gray-200" data-testid="video-gen-placeholder">
                <Film size={32} className="text-gray-400 mx-auto mb-2" />
                <p className="text-sm font-bold text-gray-600">Video Generation</p>
                <p className="text-xs text-gray-500 mt-1">Configure AI_VIDEO_PROVIDER to enable video rendering.</p>
                <p className="text-[10px] text-gray-400 mt-2">Your strategy, script, and scene plan are saved and ready.</p>
              </div>

              <button onClick={() => { toast.success('Project saved!'); navigate(`/content-studio/${productId}`); }}
                className="w-full h-12 bg-[#128C7E] text-white rounded-xl font-bold text-sm active:scale-95" data-testid="save-project-btn">
                Save & Return to Content Studio
              </button>
            </>
          )}
        </div>
      </div>
      <BottomNav />
    </div>
  );
};
